import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { gallery } from '../mock';

export const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'exterior', label: 'Exterior' },
    { id: 'interior', label: 'Interior' },
    { id: 'views', label: 'Views' }
  ];

  const filteredGallery = selectedCategory === 'all'
    ? gallery
    : gallery.filter(img => img.category === selectedCategory);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const navigate = (direction) => {
    if (direction === 'next') {
      setCurrentIndex((currentIndex + 1) % filteredGallery.length);
    } else {
      setCurrentIndex((currentIndex - 1 + filteredGallery.length) % filteredGallery.length);
    }
  };

  return (
    <section id="gallery" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-[#FBFAF7]">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header - Mobile Optimized */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-[#071936] mb-3 sm:mb-4">
            Gallery
          </h2>
          <p className="text-base sm:text-lg text-[#6B7280]">
            See inside The Tullaghan Pod
          </p>
        </div>

        {/* Category Filter - Mobile Scrollable */}
        <div className="flex gap-2 sm:gap-3 md:gap-4 mb-8 sm:mb-10 md:mb-12 overflow-x-auto pb-2 scrollbar-hide justify-center">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex-shrink-0 px-5 sm:px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ease-out ${
                selectedCategory === category.id
                  ? 'bg-[#2AA7A1] text-white shadow-[0_2px_8px_rgba(42,167,161,0.2)]'
                  : 'bg-white text-[#1F2937] hover:bg-[#DDF4F1] hover:-translate-y-[1px] hover:shadow-[0_2px_6px_rgba(0,0,0,0.06)]'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid - Mobile Optimized */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-7xl mx-auto">
          {filteredGallery.map((image, index) => (
            <div
              key={image.url}
              onClick={() => openLightbox(index)}
              className="group relative aspect-[4/3] rounded-lg overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-500"
            >
              <img
                src={`${image.url}?w=600&q=80`}
                alt={image.caption}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071936]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                  <p className="text-white text-xs sm:text-sm font-medium">{image.caption}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox - Mobile Optimized */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 sm:top-6 right-4 sm:right-6 text-white/80 hover:text-white transition-colors z-10 p-2"
          >
            <X size={28} strokeWidth={1.5} />
          </button>

          <button
            onClick={() => navigate('prev')}
            className="absolute left-2 sm:left-6 text-white/80 hover:text-white transition-colors z-10 p-2"
          >
            <ChevronLeft size={40} strokeWidth={1.5} />
          </button>

          <div className="max-w-7xl max-h-[90vh] px-12 sm:px-20">
            <img
              src={`${filteredGallery[currentIndex].url}?w=1920&q=90`}
              alt={filteredGallery[currentIndex].caption}
              className="max-w-full max-h-[85vh] object-contain rounded"
            />
            <p className="text-white/70 text-center mt-3 sm:mt-4 text-sm sm:text-base px-4">
              {filteredGallery[currentIndex].caption}
            </p>
          </div>

          <button
            onClick={() => navigate('next')}
            className="absolute right-2 sm:right-6 text-white/80 hover:text-white transition-colors z-10 p-2"
          >
            <ChevronRight size={40} strokeWidth={1.5} />
          </button>

          <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-xs sm:text-sm">
            {currentIndex + 1} / {filteredGallery.length}
          </div>
        </div>
      )}
    </section>
  );
};
